import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import { fetchWardsThunk } from "../../../store/slices/wardsSlice";
import {
  fetchDisconnectionReportThunk,
  clearDisconnectionReport,
} from "../../../store/slices/disconnectionReportSlice";


import { printTableReport } from "./reportPrintUtils";

import "./ReportPage.css";

// Date helpers
const pad2 = (n) => String(n).padStart(2, "0");

const toIndiaDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";

  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;

  const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;

  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const diffInDays = (from, to) => {
  const fromD = new Date(from);
  const toD = new Date(to);

  fromD.setHours(0, 0, 0, 0);
  toD.setHours(0, 0, 0, 0);

  const ms = toD.getTime() - fromD.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

const DisconnectionReportPage = () => {
  const dispatch = useDispatch();

  const wards = useSelector((s) => s.wards?.list || []);
  const { rows = [], loading, error } = useSelector(
    (s) => s.disconnectionReport || {}
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [wardId, setWardId] = useState("");
  const [minBalance, setMinBalance] = useState(500);

  const [search, setSearch] = useState("");
  const [dateError, setDateError] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    dispatch(fetchWardsThunk());
  }, [dispatch]);

  useEffect(() => {
    const today = getToday();

    if (!fromDate && !toDate) {
      setDateError("");
      return;
    }

    if (fromDate) {
      if (fromDate > today) {
        setDateError("From Date cannot be future");
        return;
      }

      const daysFromToday = diffInDays(fromDate, today);
      if (daysFromToday > 30) {
        setDateError("From Date to Today must be within 30 days");
        return;
      }
    }

    if (toDate) {
      if (toDate > today) {
        setDateError("To Date cannot be future");
        return;
      }

      if (fromDate && toDate < fromDate) {
        setDateError("To Date must be greater than or equal to From Date");
        return;
      }
    }

    setDateError("");
  }, [fromDate, toDate]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const searchedRows = !q
      ? [...rows]
      : rows.filter((r) => {
          const searchableText = `
            ${r.wb_id}
            ${r.qr_code}
            ${r.door_no}
            ${r.family_head}
            ${r.ward_name}
            ${r.balance}
            ${toIndiaDate(r.disconnection_date)}
            ${toIndiaDate(r.bill_date)}
            ${r.remarks}
          `.toLowerCase();

          return searchableText.includes(q);
        });

    if (!sortField) {
      return searchedRows;
    }

    const numericFields = ["wb_id", "balance"];
    const dateFields = ["disconnection_date", "bill_date"];

    return [...searchedRows].sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];

      if (numericFields.includes(sortField)) {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);

        return sortDirection === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (dateFields.includes(sortField)) {
        const aTime = aValue ? new Date(aValue).getTime() : 0;
        const bTime = bValue ? new Date(bValue).getTime() : 0;

        return sortDirection === "asc"
          ? aTime - bTime
          : bTime - aTime;
      }

      const comparison = String(aValue || "").localeCompare(
        String(bValue || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [rows, search, sortField, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate, wardId, minBalance, search, rows.length]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    const today = getToday();

    if (!fromDate || !toDate) {
      setDateError("Please select From Date and To Date");
      return;
    }

    if (fromDate > today) {
      setDateError("From Date cannot be future");
      return;
    }

    const daysFromToday = diffInDays(fromDate, today);
    if (daysFromToday > 30) {
      setDateError("From Date to Today must be within 30 days");
      return;
    }

    if (toDate < fromDate) {
      setDateError("To Date must be greater than or equal to From Date");
      return;
    }

    if (toDate > today) {
      setDateError("To Date cannot be future");
      return;
    }

    setDateError("");

    dispatch(
      fetchDisconnectionReportThunk({
        from_date: fromDate,
        to_date: toDate,
        ward_id: wardId,
        min_balance: minBalance,
      })
    );
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setWardId("");
    setMinBalance(500);
    setSearch("");
    setDateError("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);
    dispatch(clearDisconnectionReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      "#": idx + 1,
      "House Code": r.qr_code,
      "Door No": r.door_no,
      Name: r.family_head,
      Ward: r.ward_name,
      Balance: r.balance,
      "Disconnection Date": toIndiaDate(r.disconnection_date),
      "Bill Date": toIndiaDate(r.bill_date),
      Remarks: r.remarks || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Disconnection Report");
    XLSX.writeFile(wb, "disconnection_report.xlsx");
  };

  const exportPDF = () => {
    const data = filteredRows.map((r, idx) => ({
      "#": idx + 1,
      "House Code": r.qr_code ?? "",
      "Door No": r.door_no ?? "",
      Name: r.family_head ?? "",
      Ward: r.ward_name ?? "",
      Balance: r.balance ?? "",
      "Disconnection Date": toIndiaDate(r.disconnection_date),
      "Bill Date": toIndiaDate(r.bill_date),
      Remarks: r.remarks || "",
    }));

    printTableReport({
      title: "Disconnection List - Report",
fileName: "disconnection_report.pdf",
      data,
      metaLines: [`Total Records: ${filteredRows.length}`],
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  const today = getToday();

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">Disconnection List - Report</div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? <div className="alert alert-danger mb-3">{error}</div> : null}
          {dateError ? (
            <div className="alert alert-warning mb-3">{dateError}</div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  max={today}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  max={today}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">Ward</Form.Label>
                <Form.Select
                  value={wardId}
                  onChange={(e) => setWardId(e.target.value)}
                >
                  <option value="">Select Ward</option>
                  {wards.map((w) => (
                    <option key={w.ward_id} value={String(w.ward_id)}>
                      {w.ward_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">Min Balance</Form.Label>
                <Form.Control
                  type="number"
                  value={minBalance}
                  onChange={(e) => setMinBalance(Number(e.target.value || 0))}
                />
              </Col>

              <Col lg={12} className="d-flex align-items-end">
                <div className="report-filter-actions">
                  <Button
                    variant="primary"
                    className="report-btn-primary"
                    onClick={handleLoad}
                    disabled={loading || !!dateError}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" /> Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    variant="outline-secondary"
                    className="report-btn"
                    onClick={handleClear}
                  >
                    <RefreshCcw size={16} className="me-2" />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              Disconnections{" "}
              <span className="report-count">
                ({total.toLocaleString()} records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search house / door / name / ward / remarks..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={!filteredRows.length}
              >
                <Download size={16} className="me-2" />
                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table hover className="mb-0 report-table">
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "wb_id" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("wb_id")}
                      >
                        # {renderSortIcon("wb_id")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "qr_code" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("qr_code")}
                      >
                        House Code {renderSortIcon("qr_code")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "door_no" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("door_no")}
                      >
                        Door No {renderSortIcon("door_no")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "family_head" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("family_head")}
                      >
                        Name {renderSortIcon("family_head")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "ward_name" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("ward_name")}
                      >
                        Ward {renderSortIcon("ward_name")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "balance" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("balance")}
                      >
                        Balance {renderSortIcon("balance")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "disconnection_date" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("disconnection_date")}
                      >
                        Disconnection Date {renderSortIcon("disconnection_date")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "bill_date" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("bill_date")}
                      >
                        Bill Date {renderSortIcon("bill_date")}
                      </button>
                    </th>

                    <th>
                      <button
                        type="button"
                        className={`report-sort-btn ${
                          sortField === "remarks" ? "is-active" : ""
                        }`}
                        onClick={() => handleSort("remarks")}
                      >
                        Remarks {renderSortIcon("remarks")}
                      </button>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map((r, idx) => (
                      <tr key={String(r.wb_id || idx)}>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>{r.qr_code}</td>
                        <td>{r.door_no}</td>
                        <td>{r.family_head}</td>
                        <td>{r.ward_name}</td>
                        <td>{r.balance}</td>
                        <td>{toIndiaDate(r.disconnection_date)}</td>
                        <td>{toIndiaDate(r.bill_date)}</td>
                        <td>{r.remarks}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>
                <Form.Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="report-pagination">{renderPagination()}</div>

              <div className="report-page-info">
                Page {currentPage} / {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DisconnectionReportPage;